import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Ban, ShieldOff, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BlockedUsersList
 *
 * Settings-panel surface for the new block/unblock endpoints. Shows every
 * user the current account has blocked — regardless of whether they're still
 * in the contacts list (handy after a panic-block on someone you've since
 * forgotten) — and exposes both per-row and mass-unblock actions.
 *
 * The mass-unblock is gated behind an AlertDialog because reversing multiple
 * blocks at once is easy to do by accident and hard to undo.
 */
export const BlockedUsersList = () => {
  // `blocked` is the source of truth — array of {id, username?}. Username is
  // best-effort resolved from the contacts list; if the blocked user isn't
  // a contact anymore we still show their id so they're unblockable.
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [blockedRes, contactsRes] = await Promise.all([
        axios.get(`${API}/contacts/blocked`),
        axios.get(`${API}/contacts`).catch(() => ({ data: [] })),
      ]);
      const byId = new Map(
        (contactsRes.data || []).map((c) => [c.id, c.username])
      );
      setBlocked(
        (blockedRes.data || []).map((id) => ({
          id,
          username: byId.get(id) || null,
        }))
      );
    } catch (e) {
      toast.error('Could not load blocked users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const unblockOne = async (id) => {
    const prev = blocked;
    setBlocked((s) => s.filter((u) => u.id !== id));
    try {
      await axios.post(`${API}/contacts/${id}/unblock`);
      toast.success('Unblocked');
    } catch {
      setBlocked(prev);
      toast.error('Unblock failed');
    }
  };

  const unblockAll = async () => {
    if (blocked.length === 0) return;
    setClearing(true);
    const prev = blocked;
    setBlocked([]);
    try {
      // Sequential keeps the UI-side toast count accurate and avoids
      // stampeding the backend on very long lists.
      for (const u of prev) {
        await axios.post(`${API}/contacts/${u.id}/unblock`);
      }
      toast.success(`Unblocked ${prev.length} user${prev.length === 1 ? '' : 's'}`);
    } catch {
      setBlocked(prev);
      toast.error('Mass-unblock failed — please retry');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="blocked-users-list">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-300">
          <Ban className="w-5 h-5" />
          <span className="font-medium">Blocked users</span>
          <span className="text-xs text-slate-500">({blocked.length})</span>
        </div>
        {blocked.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                disabled={clearing}
                className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1 disabled:opacity-40"
                data-testid="unblock-all-btn"
              >
                <ShieldOff className="w-3 h-3" />
                Unblock all
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-card border-border">
              <AlertDialogHeader>
                <AlertDialogTitle>Unblock all {blocked.length} users?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">
                  Every user you've blocked will be able to message you again
                  immediately. You can re-block anyone individually from the
                  contact list.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="unblock-all-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={unblockAll}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="unblock-all-confirm"
                >
                  Unblock all
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      {loading ? (
        <p className="text-xs text-slate-500 italic">Loading…</p>
      ) : blocked.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-slate-500 px-3 py-3 rounded-md border border-border bg-background/40">
          <Users className="w-4 h-4" />
          You haven't blocked anyone. Blocked users can't send you messages.
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border bg-background/40">
          {blocked.map((u) => (
            <li
              key={u.id}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
              data-testid={`blocked-row-${u.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-slate-200 truncate">
                  {u.username || (
                    <span className="italic text-slate-400">
                      Unknown contact
                    </span>
                  )}
                </p>
                <p className="font-mono text-[10px] text-slate-500 truncate">
                  {u.id}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => unblockOne(u.id)}
                className="h-7 text-xs text-slate-400 hover:text-slate-200"
                data-testid={`unblock-row-${u.id}`}
              >
                <ShieldOff className="w-3 h-3 mr-1" />
                Unblock
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BlockedUsersList;
